import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/api";
import { FaEdit, FaTrashAlt, FaSearch } from "react-icons/fa";
import Swal from "sweetalert2";
import "./AllSchemes.css";

const AllSchemes = () => {
  const [schemes, setSchemes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [openSchemes, setOpenSchemes] = useState({});
  const [openCategories, setOpenCategories] = useState({});
  const [openSubs, setOpenSubs] = useState({});

  const navigate = useNavigate();

  const toggleScheme = (id) => {
    setOpenSchemes((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleCategory = (key) => {
    setOpenCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSub = (key) => {
    setOpenSubs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* LOAD ALL SCHEMES */
  const fetchSchemes = async () => {
    try {
      const res = await API.get("/schemes");
      const sorted = res.data.sort((a, b) => a.order - b.order);
      setSchemes(sorted);
    } catch (err) {
      console.error("Error fetching schemes:", err);
    }
  };

  useEffect(() => {
    fetchSchemes();
  }, []);

  /* MOVE UP / DOWN LOGIC */
  const moveScheme = async (schemeId, direction) => {
    const index = schemes.findIndex((s) => s._id === schemeId);
    if (index < 0) return;

    let newOrderList = [...schemes];

    if (direction === "up" && index > 0) {
      [newOrderList[index - 1], newOrderList[index]] = [
        newOrderList[index],
        newOrderList[index - 1],
      ];
    }

    if (direction === "down" && index < schemes.length - 1) {
      [newOrderList[index + 1], newOrderList[index]] = [
        newOrderList[index],
        newOrderList[index + 1],
      ];
    }

    newOrderList = newOrderList.map((item, i) => ({
      ...item,
      order: i,
    }));

    await Promise.all(
      newOrderList.map((item) =>
        API.put(`/schemes/${item._id}/order`, { order: item.order })
      )
    );

    fetchSchemes();
  };

  /* DELETE SCHEME */
  const deleteScheme = async (id) => {
    const confirmed = await Swal.fire({
      title: "Delete Scheme?",
      text: "This will delete the entire scheme!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmed.isConfirmed) return;

    try {
      await API.delete(`/schemes/${id}`);
      Swal.fire("Deleted!", "Scheme deleted successfully.", "success");
      fetchSchemes();
    } catch {
      Swal.fire("Error!", "Failed to delete scheme.", "error");
    }
  };

  /* DELETE CATEGORY */
  const deleteCategory = async (schemeId, categoryName) => {
    const confirmed = await Swal.fire({
      title: "Delete Category?",
      text: `Delete category "${categoryName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmed.isConfirmed) return;

    try {
      await API.delete(
        `/schemes/${schemeId}/category/${encodeURIComponent(categoryName)}`
      );
      Swal.fire("Deleted!", "Category deleted successfully.", "success");
      fetchSchemes();
    } catch {
      Swal.fire("Error!", "Failed to delete category.", "error");
    }
  };

  /* DELETE SUBCATEGORY */
  const deleteSubcategory = async (schemeId, categoryName, subName) => {
    const confirmed = await Swal.fire({
      title: "Delete Subcategory?",
      text: `Delete "${subName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmed.isConfirmed) return;

    try {
      await API.delete(
        `/schemes/${schemeId}/category/${encodeURIComponent(
          categoryName
        )}/subcategory/${encodeURIComponent(subName)}`
      );
      Swal.fire("Deleted!", "Subcategory deleted.", "success");
      fetchSchemes();
    } catch {
      Swal.fire("Error!", "Failed to delete subcategory.", "error");
    }
  };

  /* UPDATE VISIBILITY */
  const updateVisibility = (schemeId, payload) =>
    API.put(`/schemes/${schemeId}/visibility`, payload);

  const handleSubVisibility = async (
    schemeId,
    categoryName,
    subName,
    field,
    newValue
  ) => {
    await updateVisibility(schemeId, {
      level: "subcategory",
      categoryName,
      subName,
      field,
      value: newValue,
    });

    fetchSchemes();
  };

  /* SEARCH LOGIC */
  const highlightMatch = (text, term) => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, "gi");
    return text.split(regex).map((part, i) =>
      part.toLowerCase() === term.toLowerCase() ? (
        <mark key={i}>{part}</mark>
      ) : (
        part
      )
    );
  };

  const filteredSchemes = schemes
    .map((scheme) => {
      const term = searchTerm.toLowerCase();
      const mainMatch = scheme.mainCategory.toLowerCase().includes(term);

      const matchedCategories = scheme.categories
        .map((cat) => {
          const catMatch = cat.name.toLowerCase().includes(term);
          const matchedSub = cat.subcategories.filter((sub) =>
            sub.name.toLowerCase().includes(term)
          );

          if (catMatch || matchedSub.length)
            return {
              ...cat,
              subcategories: matchedSub.length ? matchedSub : cat.subcategories,
            };

          return null;
        })
        .filter(Boolean);

      if (mainMatch || matchedCategories.length)
        return { ...scheme, categories: matchedCategories };

      return null;
    })
    .filter(Boolean);

  return (
    <div className="schemes-wrapper container-fluid px-2 px-md-4">
      <div className="schemes-card shadow-lg p-2 p-md-3">

        {/* HEADER */}
        <div className="row align-items-center mb-3 g-2">
          <div className="col-12 col-md-6">
            <h3 className="title mb-0 text-center text-md-start">
              All Existing Cards
            </h3>
          </div>

          <div className="col-12 col-md-6 d-flex justify-content-center justify-content-md-end">
            <div
              className="search-bar d-flex align-items-center w-100"
              style={{ maxWidth: "300px" }}
            >
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by name..."
                className="form-control ms-2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* LIST */}
        {filteredSchemes.length === 0 ? (
          <p className="no-data">No matching results found.</p>
        ) : (
          filteredSchemes.map((s) => (
            <div key={s._id} className="scheme-box shadow-sm">
              <h5 className="main-category">
                {/* TOGGLE ICON */}
                <span
                  className="dropdown-icon mx-2"
                  onClick={() => toggleScheme(s._id)}
                >
                  {openSchemes[s._id] ? "▼" : "▶"}
                </span>

                {highlightMatch(s.mainCategory, searchTerm)}

                {/* RIGHT SIDE BUTTONS */}
                <span
                  className="
                    d-flex flex-wrap gap-2 justify-content-end 
                    align-items-center mt-2 mt-md-0
                  "
                >
                  {/* ORDER CONTROLS */}
                  <div className="d-flex align-items-center gap-1">

                    {/* UP ARROW (Bootstrap Icon) */}
                    <button
                      className="btn btn-sm btn-light py-0 px-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveScheme(s._id, "up");
                      }}
                    >
                      <i className="bi bi-arrow-up"></i>
                    </button>

                    {/* DOWN ARROW (Bootstrap Icon) */}
                    <button
                      className="btn btn-sm btn-light py-0 px-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveScheme(s._id, "down");
                      }}
                    >
                      <i className="bi bi-arrow-down"></i>
                    </button>

                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ width: "50px" }}
                      value={s.order}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        API.put(`/schemes/${s._id}/order`, {
                          order: Number(e.target.value),
                        }).then(fetchSchemes)
                      }
                    />
                  </div>

                  {/* HOME CHECKBOX */}
                  <label className="small me-1">
                    <input
                      type="checkbox"
                      checked={s.showInSchemeList}
                      onChange={(e) =>
                        updateVisibility(s._id, {
                          level: "scheme",
                          field: "showInSchemeList",
                          value: e.target.checked,
                        }).then(fetchSchemes)
                      }
                    />{" "}
                    Home
                  </label>

                  {/* STATE CHECKBOX */}
                  <label className="small me-2">
                    <input
                      type="checkbox"
                      checked={s.showInState}
                      onChange={(e) =>
                        updateVisibility(s._id, {
                          level: "scheme",
                          field: "showInState",
                          value: e.target.checked,
                        }).then(fetchSchemes)
                      }
                    />{" "}
                    State
                  </label>

                  {/* EDIT & DELETE ICONS */}
                  <FaEdit
                    className="icon edit"
                    onClick={() => navigate(`/edit-scheme/${s._id}`)}
                  />

                  <FaTrashAlt
                    className="icon delete"
                    onClick={() => deleteScheme(s._id)}
                  />
                </span>
              </h5>

              {/* COLLAPSE SECTION */}
              {openSchemes[s._id] && (
                <div className="ps-3">
                  {s.categories.map((cat, idx) => {
                    const catKey = `${s._id}-${cat.name}`;

                    return (
                      <div key={idx} className="category-section">

                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center category-header">

                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="dropdown-icon"
                              onClick={() => toggleCategory(catKey)}
                            >
                              {openCategories[catKey] ? "▼" : "▶"}
                            </span>

                            <strong>
                              {idx + 1}. {highlightMatch(cat.name, searchTerm)}
                            </strong>
                          </div>

                          <div className="d-flex align-items-center gap-3 mt-2 mt-md-0">

                            <label className="small me-1">
                              <input
                                type="checkbox"
                                checked={cat.showInSchemeList}
                                onChange={(e) =>
                                  updateVisibility(s._id, {
                                    level: "category",
                                    categoryName: cat.name,
                                    field: "showInSchemeList",
                                    value: e.target.checked,
                                  }).then(fetchSchemes)
                                }
                              />{" "}
                              Home
                            </label>

                            <label className="small me-2">
                              <input
                                type="checkbox"
                                checked={cat.showInState}
                                onChange={(e) =>
                                  updateVisibility(s._id, {
                                    level: "category",
                                    categoryName: cat.name,
                                    field: "showInState",
                                    value: e.target.checked,
                                  }).then(fetchSchemes)
                                }
                              />{" "}
                              State
                            </label>

                            <FaEdit
                              className="icon edit"
                              onClick={() =>
                                navigate(
                                  `/edit-category/${s._id}/${encodeURIComponent(
                                    cat.name
                                  )}`
                                )
                              }
                            />

                            <FaTrashAlt
                              className="icon delete"
                              onClick={() =>
                                deleteCategory(s._id, cat.name)
                              }
                            />
                          </div>
                        </div>

                        {/* SUBCATEGORIES */}
                        {openCategories[catKey] && (
                          <ul className="sub-list mt-2 ps-3">
                            {cat.subcategories.map((sub, subIdx) => {
                              const subKey = `${catKey}-${sub.name}`;

                              return (
                                <li key={subIdx}>
                                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center subheader">

                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        className="dropdown-icon"
                                        onClick={() => toggleSub(subKey)}
                                      >
                                        {openSubs[subKey] ? "▼" : "▶"}
                                      </span>

                                      <span>
                                        {idx + 1}.{subIdx + 1}.{" "}
                                        {highlightMatch(sub.name, searchTerm)}
                                      </span>
                                    </div>

                                    <div className="d-flex align-items-center gap-3 mt-2 mt-md-0">

                                      <label className="small me-1">
                                        <input
                                          type="checkbox"
                                          checked={sub.showInSchemeList}
                                          onChange={(e) =>
                                            handleSubVisibility(
                                              s._id,
                                              cat.name,
                                              sub.name,
                                              "showInSchemeList",
                                              e.target.checked
                                            )
                                          }
                                        />{" "}
                                        Home
                                      </label>

                                      <label className="small me-2">
                                        <input
                                          type="checkbox"
                                          checked={sub.showInState}
                                          onChange={(e) =>
                                            handleSubVisibility(
                                              s._id,
                                              cat.name,
                                              sub.name,
                                              "showInState",
                                              e.target.checked
                                            )
                                          }
                                        />{" "}
                                        State
                                      </label>

                                      <FaEdit
                                        className="icon edit"
                                        onClick={() =>
                                          navigate(
                                            `/edit-subcategory/${s._id}/${encodeURIComponent(
                                              cat.name
                                            )}/${encodeURIComponent(
                                              sub.name
                                            )}`
                                          )
                                        }
                                      />

                                      <FaTrashAlt
                                        className="icon delete"
                                        onClick={() =>
                                          deleteSubcategory(
                                            s._id,
                                            cat.name,
                                            sub.name
                                          )
                                        }
                                      />
                                    </div>
                                  </div>

                                  {openSubs[subKey] && (
                                    <div className="ps-4 text-muted small mt-1">
                                      <div>
                                        Apply Link: {sub.applyLink || "N/A"}
                                      </div>
                                      <div>
                                        YouTube Link: {sub.youtubeLink || "N/A"}
                                      </div>
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AllSchemes;
